/**
 * OrderApprovalPage
 * Public page for customers to approve their delivered orders via email link
 * URL: /orders/approve?orderId=123&token=abc123
 *
 * @author SAUBHAGYA Development Team
 * @since Phase 2 - Order Approval Workflow
 */

import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  CheckCircle2,
  XCircle,
  Package,
  Truck,
  Calendar,
  MapPin,
  AlertTriangle,
  Loader2,
  ShieldCheck,
  Mail,
  Phone,
  Building2,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { orderService, type ApprovalResponse } from '@/services/commerce/orderService';

// SAUBHAGYA Logo Component
function SaubhagyaLogo() {
  return (
    <div className="flex items-center gap-3">
      <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
        <span className="text-white font-bold text-xl">S</span>
      </div>
      <div>
        <h1 className="text-2xl font-bold text-slate-800">SAUBHAGYA</h1>
        <p className="text-xs text-slate-500">Biogas Management Platform</p>
      </div>
    </div>
  );
}

type PageState = 'loading' | 'ready' | 'approving' | 'success' | 'error' | 'already_approved' | 'expired';

interface OrderInfo {
  orderId: number;
  orderNumber: string;
  buyerName: string;
  deliveryAddress: string;
  deliveredAt: string;
  totalAmount: number;
  itemCount: number;
}

export default function OrderApprovalPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const orderId = searchParams.get('orderId');
  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [approvalResult, setApprovalResult] = useState<ApprovalResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  /**
   * Validate URL parameters on mount
   */
  useEffect(() => {
    if (!orderId || !token) {
      setErrorMessage('Invalid approval link. Please check the link from your email.');
      setPageState('error');
      return;
    }

    // For now, set to ready state
    // In production, you might want to fetch order preview info here
    setOrderInfo({
      orderId: parseInt(orderId),
      orderNumber: `ORD-${orderId}`,
      buyerName: 'Customer',
      deliveryAddress: 'Loading...',
      deliveredAt: new Date().toISOString(),
      totalAmount: 0,
      itemCount: 0,
    });
    setPageState('ready');
  }, [orderId, token]);

  /**
   * Handle approve button click
   */
  async function handleApprove() {
    if (!orderId || !token) return;

    setPageState('approving');

    try {
      const result = await orderService.customerApproveDelivery(parseInt(orderId), token);
      setApprovalResult(result);
      setPageState('success');
    } catch (error: any) {
      const message = error.message || 'Failed to approve order';

      // Check for specific error cases
      if (message.includes('already approved') || message.includes('APPROVED')) {
        setPageState('already_approved');
      } else if (message.includes('expired') || message.includes('token')) {
        setErrorMessage('This approval link has expired. Please contact support.');
        setPageState('expired');
      } else if (message.includes('status')) {
        setErrorMessage('This order is not eligible for approval. It may have already been processed.');
        setPageState('error');
      } else {
        setErrorMessage(message);
        setPageState('error');
      }
    }
  }

  // Loading State
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-amber-50 to-slate-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <Loader2 className="h-12 w-12 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading order details...</p>
        </motion.div>
      </div>
    );
  }

  // Success State
  if (pageState === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-green-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="border-2 border-green-200 shadow-xl">
              <CardHeader className="text-center pb-2">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                  className="mx-auto mb-4"
                >
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="h-12 w-12 text-green-600" />
                  </div>
                </motion.div>
                <CardTitle className="text-2xl text-green-800">
                  Order Approved Successfully!
                </CardTitle>
                <CardDescription className="text-green-600">
                  Thank you for confirming your delivery
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-800">
                    <strong>Order:</strong> {approvalResult?.orderNumber}
                  </p>
                  <p className="text-sm text-green-800 mt-1">
                    <strong>Status:</strong> {approvalResult?.newOrderStatus}
                  </p>
                </div>

                <Alert className="border-green-200 bg-green-50">
                  <Mail className="h-4 w-4 text-green-600" />
                  <AlertTitle className="text-green-800">Final Invoice Sent</AlertTitle>
                  <AlertDescription className="text-green-700">
                    We've sent the final invoice to your registered email address.
                  </AlertDescription>
                </Alert>

                <div className="text-center pt-4">
                  <SaubhagyaLogo />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Already Approved State
  if (pageState === 'already_approved') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-2 border-blue-200 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl text-blue-800">
                  Order Already Approved
                </CardTitle>
                <CardDescription className="text-blue-600">
                  This order has already been approved. No further action is needed.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <SaubhagyaLogo />
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Expired State
  if (pageState === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-2 border-amber-200 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="h-8 w-8 text-amber-600" />
                </div>
                <CardTitle className="text-xl text-amber-800">
                  Approval Link Expired
                </CardTitle>
                <CardDescription className="text-amber-600">
                  {errorMessage}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="border-amber-200 bg-amber-50">
                  <Phone className="h-4 w-4 text-amber-600" />
                  <AlertTitle className="text-amber-800">Need Help?</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    Please contact our support team to get a new approval link.
                  </AlertDescription>
                </Alert>
                <div className="text-center pt-4">
                  <SaubhagyaLogo />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Error State
  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-slate-50 to-red-50 py-12 px-4">
        <div className="max-w-lg mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="border-2 border-red-200 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle className="text-xl text-red-800">
                  Something Went Wrong
                </CardTitle>
                <CardDescription className="text-red-600">
                  {errorMessage}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Alert className="border-red-200 bg-red-50">
                  <Phone className="h-4 w-4 text-red-600" />
                  <AlertTitle className="text-red-800">Contact Support</AlertTitle>
                  <AlertDescription className="text-red-700">
                    If the problem persists, please contact our support team.
                  </AlertDescription>
                </Alert>
                <div className="text-center pt-4">
                  <SaubhagyaLogo />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Ready State - Main Approval UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Header */}
          <div className="text-center">
            <SaubhagyaLogo />
          </div>

          {/* Main Card */}
          <Card className="border-2 border-amber-200 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4">
              <div className="flex items-center justify-center gap-2 text-white">
                <Truck className="h-6 w-6" />
                <h2 className="text-xl font-semibold">Delivery Approval Required</h2>
              </div>
            </div>

            <CardContent className="p-6 space-y-6">
              {/* Order Info */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                  <Package className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="text-xs text-amber-600 font-medium">Order Number</p>
                    <p className="text-lg font-bold text-amber-900">
                      {orderInfo?.orderNumber || `ORD-${orderId}`}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Instructions */}
              <div className="space-y-3">
                <h3 className="font-semibold text-slate-800">Please confirm your delivery</h3>
                <p className="text-sm text-slate-600">
                  By clicking "Approve Delivery" below, you confirm that:
                </p>
                <ul className="text-sm text-slate-600 space-y-2 ml-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>You have received the goods in good condition</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>The quantity matches your order</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>You agree to process the payment as per terms</span>
                  </li>
                </ul>
              </div>

              <Separator />

              {/* Action Buttons */}
              <div className="space-y-3">
                <Button
                  onClick={handleApprove}
                  disabled={pageState === 'approving'}
                  className="w-full h-12 text-lg bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg"
                >
                  {pageState === 'approving' ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-5 w-5 mr-2" />
                      Approve Delivery
                    </>
                  )}
                </Button>

                <p className="text-xs text-center text-slate-500">
                  Having issues? Contact our support team for assistance.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Footer */}
          <div className="text-center text-xs text-slate-500">
            <p>This is a secure page from SAUBHAGYA Biogas Management Platform</p>
            <p className="mt-1">For support, contact: support@saubhagya.com</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
